/**
 * Confirm step for "Resend verification email" (DNKADM-0444 / DNKAPI-0650). Modelled on
 * the card-removal confirm: dismissing issues NO request, only "Send email" calls the
 * endpoint, and the dialog cannot be dismissed while a send is in flight.
 *
 * The parent owns what each outcome DOES (toast, close, refetch); this dialog stays open
 * on every failure with an inline Alert (a Retry when the failure is worth repeating).
 * The address is displayed only — the server resolves the real one, staff never supply it.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { resendMemberVerification, type ResendVerificationOutcome } from '@/api/members';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { toResendVerificationErrorView, type ResendVerificationErrorView } from './membersError';

export function ResendVerificationDialog({
  contactId,
  email,
  onClose,
  onDone,
}: {
  contactId: string;
  email: string | null;
  onClose: () => void;
  onDone: (outcome: ResendVerificationOutcome) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ResendVerificationErrorView | null>(null);
  // Guards a synchronous double-click on Send: `disabled` only lands on the next
  // render, so the ref blocks the second handler in the same tick.
  const inFlight = useRef(false);

  function close() {
    if (submitting) return;
    onClose();
  }

  async function confirm() {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const outcome = await resendMemberVerification(contactId);
      if (outcome === null) {
        // An unrecognised 200 outcome is a failure, never a success.
        setError(toResendVerificationErrorView(null));
        setSubmitting(false);
        inFlight.current = false;
        return;
      }
      onDone(outcome);
    } catch (err) {
      setError(toResendVerificationErrorView(err));
      setSubmitting(false);
      inFlight.current = false;
    }
  }

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth aria-labelledby="resend-verification-title">
      <DialogTitle id="resend-verification-title" sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Resend verification email?</Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.7) }}>
          {`We'll email ${email ?? 'this member'} a new link to verify their address. Any older link stops working.`}
        </Typography>
        {error !== null && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity="error"
              role="alert"
              action={
                error.retryable ? (
                  <Button color="inherit" size="small" onClick={() => void confirm()}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {error.message}
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="mail" />
          }
          onClick={() => void confirm()}
          disabled={submitting}
        >
          Send email
        </Button>
      </DialogActions>
    </Dialog>
  );
}
